// Manager Weekly Review — executive pulse dashboard for owner/manager roles.
// Reads from GET /dashboard/manager-review (60s cached, read-only).

import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Table, ConfigProvider, Tag, Tooltip, Select, Button, Space } from "antd";
import {
  BarChart3,
  Clock,
  AlertTriangle,
  XCircle,
  Mail,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Info,
} from "lucide-react";
import { useManagerReview } from "../hooks/useManagerReview";
import { useCrmBreakpoint } from "../hooks/useCrmBreakpoint";
import { useTeamMembers } from "../hooks/useTeamMembers";
import PageHeader from "../organisms/PageHeader";

// Shared antd pagination config — only paginate past 10 rows, and hide the
// pager entirely when everything fits on a single page.
const PAGINATION = { pageSize: 10, hideOnSinglePage: true, size: "small", showSizeChanger: false };

// antd theme tokens so the Tables inherit the CRM design language instead of
// antd's stock blue/borders. Kept local to this screen.
const ANTD_THEME = {
  token: {
    colorPrimary: "var(--crm-primary, #4273D1)",
    borderRadius: 6,
    fontSize: 13,
    colorBorderSecondary: "var(--color-border-subtle, #e2e8f0)",
  },
  components: {
    Table: {
      headerBg: "var(--color-bg-muted, #f8fafc)",
      headerColor: "var(--color-text-secondary, #64748b)",
      rowHoverBg: "var(--color-bg-muted, #f8fafc)",
      cellPaddingBlock: 10,
      cellPaddingInline: 12,
    },
  },
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getUTCDay();
  const diff = day === 0 ? 6 : day - 1;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - diff));
}

function formatWeek(iso) {
  const d = new Date(iso);
  const end = new Date(d.getTime() + 6 * 86400000);
  const fmt = (dt) => dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${fmt(d)} – ${fmt(end)}`;
}

function formatDuration(ms) {
  if (!ms) return "—";
  const hours = Math.floor(ms / 3600000);
  const mins = Math.round((ms % 3600000) / 60000);
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

// ─── Skeleton Loader ───────────────────────────────────────────────────────

function Skeleton({ width = "100%", height = 16, radius = 6, style }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: radius,
        background: "var(--color-bg-muted, #f1f5f9)",
        animation: "pulse 1.8s ease-in-out infinite",
        ...style,
      }}
    />
  );
}

function SkeletonCard() {
  return (
    <div style={{ ...cardStyle, padding: 20 }}>
      <Skeleton width={80} height={12} />
      <Skeleton width={50} height={28} style={{ marginTop: 10 }} />
      <Skeleton width={120} height={12} style={{ marginTop: 8 }} />
    </div>
  );
}

// ─── Shared Styles ─────────────────────────────────────────────────────────

const cardStyle = {
  background: "var(--color-bg-surface, #fff)",
  border: "1px solid var(--color-border-subtle, #e2e8f0)",
  borderRadius: 8,
  overflow: "hidden",
};

const sectionTitleStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--color-text-secondary)",
  marginBottom: 12,
};

const kpiValueStyle = {
  fontSize: 28,
  fontWeight: 600,
  fontVariantNumeric: "tabular-nums",
  color: "var(--color-text-primary)",
  lineHeight: 1.1,
};

const kpiLabelStyle = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  marginBottom: 4,
};

// Plain-language explanations shown on hover (info icon) for every section, so
// a manager who's never seen this screen understands what they're looking at.
const SECTION_INFO = {
  "Deals in Pipeline": "How many active deals are currently sitting in your pipeline stages right now.",
  "Stuck Deals": "Deals that haven't moved to a new stage for the number of days you picked. They may need a nudge or a follow-up.",
  "Neglected Leads": "Open leads that have had no activity (no call, email, note or stage move) in the last 7 days, grouped by the rep who owns them.",
  "Lost This Week": "Deals marked as lost during this week, along with the reason each was lost and who owned it.",
  "Deals by Stage": "A breakdown of how your active deals are spread across each pipeline stage.",
  "Email Activity by VRM": "For each rep this week: how many emails they sent, how many they received, and how quickly on average they reply to incoming mail.",
};

// Section header with an inline info tooltip (plain-language help on hover).
function SectionHeader({ icon: Icon, title, count }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, ...sectionTitleStyle, marginBottom: 14 }}>
      <Icon size={14} style={{ opacity: 0.7 }} />
      {title}
      {SECTION_INFO[title] && (
        <Tooltip title={SECTION_INFO[title]} placement="top">
          <Info size={13} style={{ opacity: 0.5, cursor: "help", flexShrink: 0 }} />
        </Tooltip>
      )}
      {count != null && (
        <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: "var(--color-text-primary)" }}>{count}</span>
      )}
    </div>
  );
}

// ─── KPI Tile ──────────────────────────────────────────────────────────────

function KpiTile({ label, value, sub, icon: Icon, tone = "neutral" }) {
  const tones = {
    neutral: "var(--color-text-secondary)",
    amber: "#d97706",
    red: "#dc2626",
    green: "#059669",
    blue: "var(--crm-primary, #6366f1)",
  };
  const color = tones[tone] || tones.neutral;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      style={{ ...cardStyle, padding: "18px 20px", flex: "1 1 180px", minWidth: 160 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <Icon size={14} style={{ color, opacity: 0.85 }} />
        <span style={kpiLabelStyle}>{label}</span>
        {SECTION_INFO[label] && (
          <Tooltip title={SECTION_INFO[label]} placement="top">
            <Info size={12} style={{ opacity: 0.45, cursor: "help", flexShrink: 0, marginLeft: -1 }} />
          </Tooltip>
        )}
      </div>
      <div style={{ ...kpiValueStyle, color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>{sub}</div>}
    </motion.div>
  );
}

// ─── Stage Bar Chart (CSS-only) ────────────────────────────────────────────

function StageBar({ stages }) {
  const max = Math.max(...stages.map((s) => s.count), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {stages.map((s, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)", width: 120, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {s.stageName}
          </span>
          <div style={{ flex: 1, height: 18, background: "var(--color-bg-muted, #f1f5f9)", borderRadius: 4, overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${(s.count / max) * 100}%`,
                background: "var(--crm-primary, #6366f1)",
                borderRadius: 4,
                transition: "width 0.3s ease",
                minWidth: s.count > 0 ? 4 : 0,
              }}
            />
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "var(--color-text-primary)", width: 30, textAlign: "right" }}>
            {s.count}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Collapsible List Section ──────────────────────────────────────────────

function ListSection({ title, icon: Icon, items, renderItem, emptyText = "None", defaultShow = 5 }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, defaultShow);
  const hasMore = items.length > defaultShow;

  return (
    <div style={{ ...cardStyle, padding: "18px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, ...sectionTitleStyle, marginBottom: 14 }}>
        <Icon size={14} style={{ opacity: 0.7 }} />
        {title}
        <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: "var(--color-text-primary)" }}>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: "8px 0" }}>{emptyText}</div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {visible.map((item, i) => renderItem(item, i))}
          </div>
          {hasMore && (
            <button
              onClick={() => setExpanded(!expanded)}
              style={{
                marginTop: 10,
                padding: "6px 0",
                border: "none",
                background: "none",
                color: "var(--crm-primary, #6366f1)",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {expanded ? "Show less" : `View all ${items.length} \u2192`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── Email VRM Table (antd) ────────────────────────────────────────────────

const numCell = (v) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{v}</span>;

function EmailVrmTable({ data, resolveVrm }) {
  if (!data || !data.length) {
    return <div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: "8px 0" }}>No email data for this week</div>;
  }

  const columns = [
    {
      title: "VRM",
      dataIndex: "userId",
      key: "userId",
      render: (userId) => {
        const v = resolveVrm(userId);
        return (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {v.name}
            </div>
            {v.sub && (
              <div style={{ fontSize: 11, color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.sub}</div>
            )}
          </div>
        );
      },
    },
    { title: "Sent", dataIndex: "sent", key: "sent", align: "right", width: 90, sorter: (a, b) => a.sent - b.sent, render: numCell },
    { title: "Received", dataIndex: "received", key: "received", align: "right", width: 110, sorter: (a, b) => a.received - b.received, render: numCell },
    {
      title: "Avg Response",
      dataIndex: "avgResponseMs",
      key: "avgResponseMs",
      align: "right",
      width: 140,
      sorter: (a, b) => (a.avgResponseMs || 0) - (b.avgResponseMs || 0),
      render: (ms) => {
        const warn = ms && ms > 4 * 3600000; // >4h
        return <span style={{ fontVariantNumeric: "tabular-nums", color: warn ? "#dc2626" : undefined, fontWeight: warn ? 600 : undefined }}>{formatDuration(ms)}</span>;
      },
    },
  ];

  return (
    <Table
      rowKey="userId"
      size="small"
      tableLayout="fixed"
      columns={columns}
      dataSource={data}
      pagination={PAGINATION}
    />
  );
}

// ─── Stuck Deals Table (antd) ──────────────────────────────────────────────

function StuckDealsTable({ data }) {
  if (!data || !data.length) {
    return <div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: "8px 0" }}>No stuck deals — healthy pipeline</div>;
  }
  const columns = [
    {
      title: "Deal",
      key: "deal",
      render: (_, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>
            {r.leadTitle || r.leadId}
          </div>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{r.pipelineName} &rarr; {r.stageName}</div>
        </div>
      ),
    },
    {
      title: "Days in Stage",
      dataIndex: "daysInStage",
      key: "daysInStage",
      align: "right",
      width: 120,
      defaultSortOrder: "descend",
      sorter: (a, b) => a.daysInStage - b.daysInStage,
      render: (d) => (
        <Tag color={d >= 14 ? "error" : "warning"} style={{ fontVariantNumeric: "tabular-nums", marginInlineEnd: 0 }}>
          {d}d
        </Tag>
      ),
    },
  ];
  return (
    <Table
      rowKey={(r) => `${r.leadId}-${r.stageName}`}
      size="small"
      tableLayout="fixed"
      columns={columns}
      dataSource={data}
      pagination={PAGINATION}
    />
  );
}

// ─── Lost Leads Table (antd) ───────────────────────────────────────────────

function LostLeadsTable({ data }) {
  if (!data || !data.length) {
    return <div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: "8px 0" }}>No deals lost this week</div>;
  }
  const columns = [
    {
      title: "Lead",
      key: "lead",
      render: (_, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>
            {r.title}
          </div>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", whiteSpace: "normal", wordBreak: "break-word", marginTop: 2 }}>
            {r.lostReason || "No reason given"}
            {r.ownerEmail ? <span style={{ opacity: 0.75 }}>{" · "}{r.ownerEmail}</span> : null}
          </div>
        </div>
      ),
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      align: "right",
      width: 110,
      sorter: (a, b) => (a.value || 0) - (b.value || 0),
      render: (v) => v > 0
        ? <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: "#dc2626" }}>{v.toLocaleString()}</span>
        : <span style={{ color: "var(--color-text-secondary)" }}>—</span>,
    },
  ];
  return (
    <Table
      rowKey={(r) => r.leadId}
      size="small"
      tableLayout="fixed"
      columns={columns}
      dataSource={data}
      pagination={PAGINATION}
    />
  );
}

// ─── Main Component ────────────────────────────────────────────────────────

export default function ManagerReview() {
  const { isMobile, pagePad } = useCrmBreakpoint();
  const [weekOffset, setWeekOffset] = useState(0);
  const [stuckDays, setStuckDays] = useState(7);

  const weekStart = useMemo(() => {
    const base = startOfWeek(new Date());
    const shifted = new Date(base.getTime() + weekOffset * 7 * 86400000);
    return shifted.toISOString();
  }, [weekOffset]);

  const { data, isLoading, isError, error, refetch, isFetching } = useManagerReview({ weekStart, stuckDays });

  // Resolve a raw userId → human name/email using the CRM member roster
  // (cached 5min). Falls back to a shortened id so we never render a bare
  // ObjectId to the user.
  const { byId: membersById } = useTeamMembers();
  const resolveVrm = useMemo(() => (userId) => {
    if (!userId) return { name: "Unassigned", sub: null };
    const m = membersById.get(String(userId));
    if (m) {
      const full = [m.firstName, m.lastName].filter(Boolean).join(" ").trim();
      const name = full || m.email || m.stubEmail || `User ${String(userId).slice(-6)}`;
      const sub = full && m.email ? m.email : null;
      return { name, sub };
    }
    return { name: `User ${String(userId).slice(-6)}`, sub: null };
  }, [membersById]);

  const dealsByStage = data?.dealsByStage || [];
  const stuckDeals = data?.stuckDeals || [];
  const neglectedByVrm = data?.neglectedByVrm || [];
  const lostLeads = data?.lostLeads || [];
  const emailByVrm = data?.emailByVrm || [];

  const totalDeals = dealsByStage.reduce((s, d) => s + d.count, 0);
  const totalNeglected = neglectedByVrm.reduce((s, g) => s + g.leads.length, 0);

  // ─── Error state ──────────────────────────────────────────────────────
  if (isError) {
    const msg = error?.response?.data?.error || error?.message || "Failed to load review";
    const is403 = error?.response?.status === 403;
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
        <div style={{ textAlign: "center", maxWidth: 360 }}>
          <AlertTriangle size={32} style={{ color: "#d97706", marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 6 }}>
            {is403 ? "Access Restricted" : "Something went wrong"}
          </div>
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>{msg}</div>
        </div>
      </div>
    );
  }

  return (
    <ConfigProvider theme={ANTD_THEME}>
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <style>{`@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } } @keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <PageHeader
        title="Weekly Review"
        subtitle={`Manager pulse — ${formatWeek(weekStart)}`}
        actions={
          <Space wrap size={8}>
            {/* Week navigator */}
            <Space.Compact size="small">
              <Tooltip title="Previous week">
                <Button icon={<ChevronLeft size={14} />} onClick={() => setWeekOffset((w) => w - 1)} />
              </Tooltip>
              <Button onClick={() => setWeekOffset(0)} disabled={weekOffset === 0}>This week</Button>
              <Tooltip title="Next week">
                <Button icon={<ChevronRight size={14} />} onClick={() => setWeekOffset((w) => Math.min(w + 1, 0))} disabled={weekOffset >= 0} />
              </Tooltip>
            </Space.Compact>

            {/* Stuck days selector */}
            <Tooltip title="Only count a deal as 'stuck' once it has sat in the same stage for at least this many days.">
              <Select
                value={stuckDays}
                onChange={(v) => setStuckDays(Number(v))}
                size="small"
                style={{ width: 140 }}
                options={[
                  { value: 3, label: "Stuck 3+ days" },
                  { value: 5, label: "Stuck 5+ days" },
                  { value: 7, label: "Stuck 7+ days" },
                  { value: 14, label: "Stuck 14+ days" },
                ]}
              />
            </Tooltip>

            {/* Refresh */}
            <Tooltip title="Refresh data">
              <Button
                size="small"
                icon={<RefreshCw size={14} style={{ animation: isFetching ? "spin 1s linear infinite" : undefined }} />}
                onClick={() => refetch()}
                loading={false}
                disabled={isFetching}
              />
            </Tooltip>
          </Space>
        }
      />

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: `8px ${pagePad}px 40px` }}>

      {/* KPI Tiles */}
      {isLoading ? (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 28 }}>
          <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
      ) : (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 28 }}>
          <KpiTile label="Deals in Pipeline" value={totalDeals} icon={BarChart3} tone="blue" />
          <KpiTile label="Stuck Deals" value={stuckDeals.length} sub={`${stuckDays}+ days in stage`} icon={Clock} tone={stuckDeals.length > 0 ? "amber" : "neutral"} />
          <KpiTile label="Neglected Leads" value={totalNeglected} sub="No activity in 7 days" icon={AlertTriangle} tone={totalNeglected > 0 ? "amber" : "neutral"} />
          <KpiTile label="Lost This Week" value={lostLeads.length} icon={XCircle} tone={lostLeads.length > 0 ? "red" : "neutral"} />
        </div>
      )}

      {/* Main Grid */}
      {isLoading ? (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
          {[1, 2, 3, 4].map((i) => <div key={i} style={{ ...cardStyle, padding: 20, minHeight: 180 }}><Skeleton height={14} width={140} /><Skeleton height={120} style={{ marginTop: 16 }} /></div>)}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
          {/* Deals by Stage */}
          <div style={{ ...cardStyle, padding: "18px 20px" }}>
            <SectionHeader icon={BarChart3} title="Deals by Stage" />
            {dealsByStage.length > 0 ? (
              <StageBar stages={dealsByStage} />
            ) : (
              <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>No pipeline data</div>
            )}
          </div>

          {/* Email by VRM */}
          <div style={{ ...cardStyle, padding: "18px 20px" }}>
            <SectionHeader icon={Mail} title="Email Activity by VRM" />
            <EmailVrmTable data={emailByVrm} resolveVrm={resolveVrm} />
          </div>

          {/* Stuck Deals */}
          <div style={{ ...cardStyle, padding: "18px 20px" }}>
            <SectionHeader icon={Clock} title="Stuck Deals" count={stuckDeals.length} />
            <StuckDealsTable data={stuckDeals} />
          </div>

          {/* Lost Leads */}
          <div style={{ ...cardStyle, padding: "18px 20px" }}>
            <SectionHeader icon={XCircle} title="Lost This Week" count={lostLeads.length} />
            <LostLeadsTable data={lostLeads} />
          </div>

          {/* Neglected by VRM — full width */}
          <div style={{ gridColumn: isMobile ? undefined : "1 / -1" }}>
            <ListSection
              title="Neglected Leads by VRM"
              icon={AlertTriangle}
              items={neglectedByVrm}
              defaultShow={3}
              emptyText="All leads have recent activity"
              renderItem={(group, i) => (
                <div key={i} style={{ padding: "8px 0", borderBottom: i < neglectedByVrm.length - 1 ? "1px solid var(--color-border-subtle, #f1f5f9)" : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
                      {group.ownerEmail || resolveVrm(group.ownerId).name}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#d97706", fontVariantNumeric: "tabular-nums" }}>
                      {group.leads.length} lead{group.leads.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {group.leads.slice(0, 5).map((lead) => (
                      <span key={lead.leadId} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: "var(--color-bg-muted, #f1f5f9)", color: "var(--color-text-secondary)", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {lead.title}
                      </span>
                    ))}
                    {group.leads.length > 5 && (
                      <span style={{ fontSize: 11, padding: "2px 8px", color: "var(--color-text-secondary)" }}>
                        +{group.leads.length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      )}
      </div>
    </div>
    </ConfigProvider>
  );
}
